/**
 * @file CasePromotion.jsx
 * @description "We don't just list your case. We promote it." for the agency marketing pages: a phone showing a
 *   sample Instagram post (the same card design the daily promo posts) next to the channels a new case goes out on
 *   (Instagram, Facebook and TikTok; local Facebook job groups; matching-provider alerts; ABA training programs).
 *   The page renders its own h2 with `agencyVisuals.promoTitle`; this renders the rest. Copy covers client cases and staff positions.
 *   Card images: public/marketing/agency/promo-*.webp, rendered from .review/social/daily-promo/card-renderer.js.
 * @module components/CasePromotion
 */
import { useTranslation } from "react-i18next";
import { SampleBadge } from "./UI";
import { SocialIcon } from "./SocialIcons";
import { BellIcon, UsersIcon, SchoolIcon, LockIcon } from "./Icons";

const NAVY = "#0F1024";
const INDIGO = "#4F46E5";
const BORDER = "rgba(226,232,240,0.9)";
const MID = "#475569";

// The phone shows a real post, which is in English on Instagram, so the caption stays English in both languages.
const POSTS = {
  case: {
    src: "/marketing/agency/promo-case-card.webp",
    caption: "NEW OPENING: RBT case in Miami, FL. Pay shown up front. Know an RBT in Miami? Send this their way.",
  },
  position: {
    src: "/marketing/agency/promo-position-card.webp",
    caption: "NOW HIRING: BCBA position in Miami, FL. Pay shown up front. Know a BCBA in Miami? Send this their way.",
  },
};

const BRAND_TILES = [
  { name: "Instagram", background: "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)" },
  { name: "Facebook", background: "#1877F2" },
  // TikTok's mark is white with a cyan and red offset.
  { name: "TikTok", background: "#000", glyph: { filter: "drop-shadow(-1px -1px 0 #25F4EE) drop-shadow(1px 1px 0 #FE2C55)" } },
];

function PhonePost({ post, alt, isMobile, t }) {
  return (
    <div style={{ position: "relative", width: isMobile ? 270 : 300, margin: isMobile ? "0 auto" : 0, background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 34, padding: 12, boxShadow: "0 18px 50px rgba(15,23,42,0.14)", boxSizing: "border-box", flexShrink: 0 }}>
      <SampleBadge style={{ position: "absolute", top: 22, right: 22 }}>{t("agencyVisuals.sample")}</SampleBadge>
      <div style={{ borderRadius: 24, overflow: "hidden", border: "1px solid #EEF2F7" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px" }}>
          <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: "50%", padding: 2, boxSizing: "border-box", background: "conic-gradient(#FEDA75, #FA7E1E, #D62976, #962FBF, #4F5BD5, #FEDA75)", flexShrink: 0 }}>
            <img src="/logo192.png" alt="" width="26" height="26" style={{ width: "100%", height: "100%", borderRadius: "50%", border: "2px solid #fff", boxSizing: "border-box", display: "block", background: "#fff" }} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>aba_cases</div>
            <div style={{ fontSize: 12, color: MID }}>Miami, Florida</div>
          </div>
        </div>
        <img src={post.src} alt={alt} width="552" height="690" loading="lazy" decoding="async" style={{ display: "block", width: "100%", height: "auto", aspectRatio: "4 / 5", background: "#1a1840" }} />
        <div aria-hidden="true" style={{ display: "flex", gap: 14, padding: "10px 12px 4px" }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z" /></svg>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2"><path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.6A8.4 8.4 0 1 1 21 11.5z" /></svg>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2"><path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4z" /></svg>
        </div>
        <p lang="en" style={{ margin: 0, fontSize: 12.5, lineHeight: 1.45, padding: "0 12px 14px", color: "#111" }}>
          <b>aba_cases</b> {post.caption}
        </p>
      </div>
    </div>
  );
}

function ChannelRow({ lead, title, line, hero, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: hero && isMobile ? "column" : "row", alignItems: hero && isMobile ? "flex-start" : "center", gap: isMobile ? 12 : 16, background: "#fff", border: hero ? `2px solid ${INDIGO}` : `1px solid ${BORDER}`, borderRadius: 16, padding: isMobile ? 14 : "16px 18px", boxShadow: hero ? "0 10px 30px rgba(79,70,229,0.14)" : "0 2px 10px rgba(15,23,42,0.05)", boxSizing: "border-box" }}>
      {lead}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 800, color: NAVY, lineHeight: 1.3 }}>{title}</div>
        <div style={{ fontSize: 14, color: MID, marginTop: 3, lineHeight: 1.45 }}>{line}</div>
      </div>
    </div>
  );
}

const iconTile = (Icon) => (
  <span aria-hidden="true" style={{ width: 44, height: 44, borderRadius: 12, background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    <Icon size={22} color={INDIGO} />
  </span>
);

/**
 * @param {object} props
 * @param {boolean} props.isMobile
 * @param {boolean} [props.staffBcba] Show a staff BCBA position (green "NOW HIRING" card) instead of an RBT client case.
 */
export default function CasePromotion({ isMobile, staffBcba = false }) {
  const { t } = useTranslation();
  const post = staffBcba ? POSTS.position : POSTS.case;
  const logoSize = isMobile ? 36 : 40;
  const brands = (
    <div aria-hidden="true" style={{ display: "flex", gap: 6, flexShrink: 0 }}>
      {BRAND_TILES.map((b) => (
        <span key={b.name} style={{ width: logoSize, height: logoSize, borderRadius: 11, background: b.background, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SocialIcon name={b.name} size={b.name === "TikTok" ? 20 : 22} style={b.glyph} />
        </span>
      ))}
    </div>
  );
  return (
    <div data-testid="case-promotion" style={{ maxWidth: 920, margin: "0 auto" }}>
      <p style={{ textAlign: "center", fontSize: isMobile ? 15 : 16, color: MID, lineHeight: 1.55, maxWidth: 560, margin: isMobile ? "-12px auto 26px" : "-16px auto 36px" }}>
        {t("agencyVisuals.promoSub")}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "300px minmax(0, 1fr)", gap: isMobile ? 26 : 44, alignItems: "center" }}>
        <PhonePost post={post} alt={t(staffBcba ? "agencyVisuals.promoCardAltPosition" : "agencyVisuals.promoCardAlt")} isMobile={isMobile} t={t} />
        <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
          <ChannelRow hero isMobile={isMobile} lead={brands} title={t("agencyVisuals.promoSocialTitle")} line={t("agencyVisuals.promoSocialLine")} />
          <ChannelRow isMobile={isMobile} lead={iconTile(UsersIcon)} title={t("agencyVisuals.promoGroupsTitle")} line={t("agencyVisuals.promoGroupsLine")} />
          <ChannelRow isMobile={isMobile} lead={iconTile(BellIcon)} title={t("agencyVisuals.promoAlertTitle")} line={t("agencyVisuals.promoAlertLine")} />
          <ChannelRow isMobile={isMobile} lead={iconTile(SchoolIcon)} title={t("agencyVisuals.promoTrainingTitle")} line={t("agencyVisuals.promoTrainingLine")} />
        </div>
      </div>
      <p style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: 8, margin: "26px auto 0", maxWidth: 640, fontSize: 14, color: MID, lineHeight: 1.5, textAlign: isMobile ? "left" : "center" }}>
        <span aria-hidden="true" style={{ display: "flex", flexShrink: 0, marginTop: 1 }}><LockIcon size={18} color={INDIGO} /></span>
        <span>{t("agencyVisuals.promoPrivacy")}</span>
      </p>
    </div>
  );
}
